import type { ReactNode } from 'react';
import {
  JOURNEY_MODULES,
  LEARN_MODULE,
  MARKETPLACE_MODULE,
  KNOWLEDGE_MODULE,
  COMMERCIALIZATION_MODULE,
  BUILD_LAB_MODULE,
  PROTOTYPING_MODULE,
  DESIGN_SKETCH_MODULE,
  INVENTION_LOG_MODULE,
} from '../modules';

/**
 * The single source of truth for the member app's navigation. The desktop
 * rail (SidebarNav) and the mobile Menu sheet (MenuSheet) both render from
 * these arrays, and section counts / "contains the current route" are derived
 * from the same `isActive` matchers, so the two views can't drift apart.
 *
 * Collections (pinned tags) and the admin-only Operator section are not here:
 * Collections is data-driven (CollectionsNav) and Operator depends on roles.
 */

/** Everything a matcher needs to decide whether a row is the current route. */
export interface NavCtx {
  location: string;
  /** ?advisor= on /strategy. */
  advisorParam: string | null;
  /** ?tag= on /ideas (a Collection is the active view). */
  collectionTagParam: string | null;
}

export interface NavItem {
  key: string;
  label: string;
  href: string;
  glyph: ReactNode;
  /** Coming soon: renders muted with a SOON label, routes to a preview page. */
  soon?: boolean;
  /** Not counted in the section's header number (e.g. a section overview link). */
  uncounted?: boolean;
  /** Native tooltip. */
  title?: string;
  isActive: (c: NavCtx) => boolean;
}

export interface NavSectionDef {
  id: string;
  label: string;
  hint: string;
  items: NavItem[];
  /** Shown under the "On the roadmap" caption in the mobile sheet. */
  roadmap?: boolean;
}

// ---------- Glyphs (14px, currentColor) ----------

const g = {
  dashboard: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <rect x="8" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  spark: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2c.5 4.5 2.5 6.5 7 7-4.5.5-6.5 2.5-7 7-.5-4.5-2.5-6.5-7-7 4.5-.5 6.5-2.5 7-7Z" />
    </svg>
  ),
  capture: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M12 3 3 7.5 12 12l9-4.5L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M3 12l9 4.5L21 12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity="0.6" />
    </svg>
  ),
  market: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.5" opacity="0.65" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    </svg>
  ),
  patent: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  finance: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  command: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>
  ),
  signals: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M3 17l5-6 4 4 6-8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 7h3v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  decompose: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="1.5" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <rect x="7.5" y="2.5" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1" opacity="0.7" />
      <rect x="2.5" y="8" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1" opacity="0.7" />
      <rect x="8" y="8" width="4.5" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  sketch: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 2.5 11.5 4.5 5 11l-2.5.5L3 9l6.5-6.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8.3 3.7 10.3 5.7" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  log: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="2.5" y="1.5" width="9" height="11" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 1.5v11" stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <path d="M6.5 4.5h3M6.5 7h3M6.5 9.5h2" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  ),
  discovery: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2 3.6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v4.4a1 1 0 0 1-1 1H5.6L3 11.4V9a1 1 0 0 1-1-1V3.6Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M4.5 5.4h5M4.5 7h3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  ),
  canvas: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2.5" width="11" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 5.5h11" stroke="currentColor" strokeWidth="1" opacity="0.55" />
      <path d="M5.5 5.5v6" stroke="currentColor" strokeWidth="1" opacity="0.55" />
      <path d="M9 5.5v6" stroke="currentColor" strokeWidth="1" opacity="0.55" />
    </svg>
  ),
  tracker: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2 4.2 3.1 5.3 5.2 3.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 4.2h4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M2 9.5 3.1 10.6 5.2 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.5 9.5h4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  notes: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 1.5h6.2L11.5 4.3v8.2h-9z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M4.5 6.5h5M4.5 9h3.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  star: <span style={{ fontSize: 11 }}>★</span>,
  glossary: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 3.2C5.8 2.4 4.3 2.2 2.6 2.4v8c1.7-.2 3.2 0 4.4.8 1.2-.8 2.7-1 4.4-.8v-8c-1.7-.2-3.2 0-4.4.8Z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
      <path d="M7 3.2v8.4" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  medal: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M4 1.5h6l-1.4 4.2" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
      <path d="M5.4 5.7 4 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="7" cy="9" r="3.4" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5.7 9.2 6.6 10 8.3 8.2" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  ),
  briefcase: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="4.5" width="11" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 4.5V3.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M1.5 7.5h11" stroke="currentColor" strokeWidth="1" opacity="0.5" />
    </svg>
  ),
  box: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.4 12.2 4.2v5.6L7 12.6 1.8 9.8V4.2L7 1.4Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M1.8 4.2 7 7l5.2-2.8M7 7v5.6" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" opacity="0.85" />
    </svg>
  ),
  vault: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2.5" width="11" height="9" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4.5V3M7 11v-1.5M4.5 7H3M11 7H9.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  ),
  revenue: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 3.5v7M9 5.5c-.4-.7-1.1-1-2-1-1 0-1.8.5-1.8 1.3 0 .7.6 1.1 1.5 1.3l.6.1c1 .2 1.7.6 1.7 1.4 0 .9-.9 1.4-1.9 1.4-1 0-1.8-.4-2.1-1.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  admin: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1.6 2.5 3.4v3.8c0 2.8 2 4.6 4.5 5.6 2.5-1 4.5-2.8 4.5-5.6V3.4L7 1.6Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <circle cx="7" cy="6.4" r="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 7.6V9.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  install: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="M8 11l4 4 4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  ),
  clock: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2.1 1.3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  kanban: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2" width="3" height="10" rx="0.8" stroke="currentColor" strokeWidth="1.2" />
      <rect x="5.5" y="2" width="3" height="7" rx="0.8" stroke="currentColor" strokeWidth="1.2" />
      <rect x="9.5" y="2" width="3" height="5" rx="0.8" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  gear: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 1.5v1.8M7 10.7v1.8M12.5 7h-1.8M3.3 7H1.5M10.9 3.1l-1.3 1.3M4.4 9.6l-1.3 1.3M10.9 10.9l-1.3-1.3M4.4 4.4 3.1 3.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
};
export const NAV_GLYPHS = g;

// ---------- Matchers shared between rows and mobile tabs ----------

/** Idea Capture: the ideas list, new-idea form, and an idea's edit/version pages. */
/** Which Business Planning feature the hub was opened for (?feature=discovery|canvas). */
function bpFeature(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('feature');
}

export function isIdeaCaptureActive({ location, collectionTagParam }: NavCtx): boolean {
  if (collectionTagParam) return false;
  if (location === '/ideas' || location === '/ideas/new') return true;
  // The bare idea route (/ideas/:id) is the Command Center, not Capture.
  if (/^\/ideas\/[^/]+$/.test(location)) return false;
  return (
    location.startsWith('/ideas/') &&
    !location.includes('/session') &&
    !location.includes('/memos/') &&
    !location.includes('/notes') &&
    !location.includes('/needs-discovery') &&
    !location.includes('/business-case') &&
    !location.includes('/search-strategy')
  );
}

const ADVISORS: Array<{ param: 'market' | 'patent' | 'finance'; label: string; routeMatch: string[]; glyph: ReactNode }> = [
  { param: 'market', label: 'Product Strategist', routeMatch: ['/needs-discovery'], glyph: g.market },
  { param: 'patent', label: 'IP Consultant', routeMatch: ['/session', '/memos/'], glyph: g.patent },
  { param: 'finance', label: 'Financial Analyst', routeMatch: ['/business-case'], glyph: g.finance },
];

// ---------- Start group (always visible, no header) ----------

export const NAV_START: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    href: '/dashboard',
    glyph: g.dashboard,
    title: 'Your home base: a daily summary of your ideas, your recommended next step, reminders, and your progress and streak.',
    isActive: (c) => c.location === '/dashboard',
  },
  {
    key: 'spark',
    label: 'Spark Ideation',
    href: '/spark',
    glyph: g.spark,
    title: 'Turn an everyday frustration into concrete, shaped business ideas worth pursuing.',
    isActive: (c) => c.location === '/spark',
  },
  {
    key: 'capture',
    label: 'Idea Capture',
    href: '/ideas',
    glyph: g.capture,
    title: 'Write down and refine a new invention idea, and keep every version as it evolves.',
    isActive: isIdeaCaptureActive,
  },
  {
    key: 'notes',
    label: 'My Notes',
    href: '/notes',
    glyph: g.notes,
    title: 'Your quick notes and your highlights on memos and lessons, in one place. Press N and switch to Note to jot one down.',
    isActive: ({ location }) => location === '/notes' || /^\/ideas\/[^/]+\/notes/.test(location),
  },
];

// ---------- Collapsible sections ----------

const journeyItems: NavItem[] = JOURNEY_MODULES.filter((m) => m.slug !== 'capture' && m.slug !== 'strategy').map(
  (m, i) => {
    const live = m.status === 'live';
    return {
      key: m.slug,
      label: m.title,
      href: live ? m.href : `/modules/${m.slug}`,
      glyph: <span>{String(i + 1).padStart(2, '0')}</span>,
      soon: !live,
      isActive: ({ location }) => {
        if (!live) return location === `/modules/${m.slug}`;
        if (m.slug === 'patent-search-engine') return location.includes('/search-strategy');
        if (m.slug === 'pathway-navigator') return location.startsWith('/pathway') || /\/ideas\/[^/]+\/pathway/.test(location);
        if (m.slug === 'drafting-workbench') return location.startsWith('/drafting') || /\/ideas\/[^/]+\/drafting/.test(location);
        return location === m.href;
      },
    };
  },
);

/** ?view= on /notes: inbox | manage (absent = every note). */
function notesView(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('view');
}

export const NAV_SECTIONS: NavSectionDef[] = [
  {
    id: 'advisors',
    label: 'My AI Advisory Board',
    hint: 'Consult AI advisors on any idea: a Product Strategist for the market, an IP Consultant for patentability, and a Financial Analyst for the business case, each ending in a memo.',
    items: [
      // The board overview (all three advisors + every idea's verdicts). Without this the page was unreachable once left.
      {
        key: 'board',
        label: 'Board overview',
        href: '/strategy',
        glyph: g.command,
        uncounted: true,
        isActive: ({ location, advisorParam }) => location === '/strategy' && !advisorParam,
      },
      ...ADVISORS.map<NavItem>((adv) => ({
        key: adv.param,
        label: adv.label,
        href: `/strategy?advisor=${adv.param}`,
        glyph: adv.glyph,
        isActive: ({ location, advisorParam }) =>
          location === '/strategy'
            ? advisorParam === adv.param
            : adv.routeMatch.some((frag) => location.includes(frag)),
      })),
    ],
  },
  {
    id: 'intelligence',
    label: 'My Intelligence Center',
    hint: "Every advisor's verdict, key figures and live signals on an idea, in one place. Open any idea for its full Command Center.",
    items: [
      {
        key: 'command',
        label: 'Command Center',
        href: '/intelligence',
        glyph: g.command,
        isActive: ({ location }) =>
          location === '/intelligence' || (/^\/ideas\/[^/]+$/.test(location) && location !== '/ideas/new'),
      },
      {
        key: 'signals',
        label: 'Signals Monitoring',
        href: '/signals',
        glyph: g.signals,
        isActive: ({ location }) => location === '/signals' || /^\/ideas\/[^/]+\/signals/.test(location),
      },
    ],
  },
  {
    id: 'journey',
    label: 'My IP Journey',
    hint: 'The step-by-step path to protecting your idea: search prior art, choose your filing pathway and IP strategy, draft your application, and track deadlines and costs.',
    items: journeyItems,
  },
  {
    id: 'design',
    label: 'My Design Workspace',
    hint: 'Turn your idea into something buildable: break it into components and materials, sketch it, and keep the invention record.',
    items: [
      {
        key: 'decompose',
        label: 'Design Decomposition',
        href: '/invention-log',
        glyph: g.decompose,
        isActive: ({ location }) => location.startsWith('/invention-log') || location.endsWith('/design'),
      },

      {
        key: 'sketch',
        label: DESIGN_SKETCH_MODULE.title,
        href: DESIGN_SKETCH_MODULE.href,
        glyph: g.sketch,
        isActive: ({ location }) => location.startsWith('/design-sketch') || location.endsWith('/sketch'),
      },
      {
        key: 'log',
        label: INVENTION_LOG_MODULE.title,
        href: '/log',
        glyph: g.log,
        isActive: ({ location }) => location === '/log' || /\/ideas\/[^/]+\/log/.test(location),
      },
    ],
  },
  {
    id: 'business',
    label: 'My Business Planning',
    hint: 'Plan the business around your invention: discover your customers, build a business model canvas, and track your progress toward launch.',
    items: [
      // The Business Planning overview (all three tools + every idea). Its own row so the hub doesn't light up Customer Discovery.
      {
        key: 'bp-overview',
        label: 'Planning overview',
        href: '/business-planning',
        glyph: g.command,
        uncounted: true,
        isActive: ({ location }) => /(^|\/)business-planning$/.test(location) && !bpFeature(),
      },
      {
        key: 'discovery',
        label: 'Customer Discovery',
        href: '/business-planning?feature=discovery',
        glyph: g.discovery,
        isActive: ({ location }) =>
          (location.includes('/business-planning') && bpFeature() === 'discovery') || location.includes('/customer-discovery'),
      },
      {
        key: 'canvas',
        label: 'Business Model Canvas',
        href: '/business-planning?feature=canvas',
        glyph: g.canvas,
        isActive: ({ location }) =>
          (location.includes('/business-planning') && bpFeature() === 'canvas') ||
          location.includes('/business-canvas') ||
          location.includes('/sample-business-model-canvas'),
      },
      {
        key: 'tracker',
        label: 'Progress Tracking',
        href: '/business-planning?feature=tracker',
        glyph: g.tracker,
        isActive: ({ location }) => (location.includes('/business-planning') && bpFeature() === 'tracker') || location.includes('#tracker') || /^\/ideas\/[^/]+\/progress/.test(location),
      },
    ],
  },
  {
    id: 'learning',
    label: 'My Learning',
    hint: 'Build your IP and entrepreneurship fundamentals: guided lessons, a plain-English glossary, and badges you earn as you learn.',
    items: [
      {
        key: 'learn',
        label: LEARN_MODULE.title,
        href: LEARN_MODULE.href,
        glyph: g.star,
        isActive: ({ location }) => location.startsWith('/learn') && !location.startsWith('/learn/glossary'),
      },
      {
        key: 'glossary',
        label: 'EntreDash Glossary',
        href: '/learn/glossary',
        glyph: g.glossary,
        isActive: ({ location }) => location.startsWith('/learn/glossary'),
      },
      {
        key: 'badges',
        label: 'Badges & Rewards',
        href: '/badges',
        glyph: g.medal,
        isActive: ({ location }) => location.startsWith('/badges') || location.startsWith('/leaderboard'),
      },
    ],
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    hint: 'Find a vetted patent attorney or agent when you need professional help, and (soon) partners to build a physical prototype.',
    items: [
      {
        key: 'practitioners',
        label: 'Patent Practitioners',
        href: MARKETPLACE_MODULE.href,
        glyph: g.briefcase,
        title: 'Find a USPTO-registered patent attorney or agent',
        isActive: ({ location }) => location.startsWith('/marketplace'),
      },
      {
        key: 'prototyping',
        label: 'Prototyping Partners',
        href: `/modules/${PROTOTYPING_MODULE.slug}`,
        glyph: g.box,
        soon: true,
        title: 'Find a prototyping service provider',
        isActive: ({ location }) => location === `/modules/${PROTOTYPING_MODULE.slug}`,
      },
    ],
  },
  {
    id: 'roadmap',
    label: 'On the roadmap',
    hint: 'Features we are building next. Open one to read what it will do and join the waitlist.',
    roadmap: true,
    items: [
      {
        key: 'vault',
        label: KNOWLEDGE_MODULE.title,
        href: `/modules/${KNOWLEDGE_MODULE.slug}`,
        glyph: g.vault,
        soon: true,
        isActive: ({ location }) => location === `/modules/${KNOWLEDGE_MODULE.slug}`,
      },
      {
        // Solution Architect and Supply Chain Expert live here as consultants,
        // not as advisor-board seats (they give no verdict).
        key: 'build-lab',
        label: BUILD_LAB_MODULE.title,
        href: `/modules/${BUILD_LAB_MODULE.slug}`,
        glyph: g.box,
        soon: true,
        isActive: ({ location }) => location === `/modules/${BUILD_LAB_MODULE.slug}`,
      },
      {
        key: 'commercialization',
        label: COMMERCIALIZATION_MODULE.title,
        href: `/modules/${COMMERCIALIZATION_MODULE.slug}`,
        glyph: g.revenue,
        soon: true,
        isActive: ({ location }) => location === `/modules/${COMMERCIALIZATION_MODULE.slug}`,
      },
    ],
  },
];

/** Admin-only section, appended when the user has the admin role. */
export const OPERATOR_SECTION: NavSectionDef = {
  id: 'operator',
  label: 'Operator',
  hint: 'Admin tools for running EntreDash: users, revenue and product metrics, content and campaigns, announcements, and support.',
  items: [
    {
      key: 'admin',
      label: 'Admin',
      href: '/admin',
      glyph: g.admin,
      isActive: ({ location }) => location.startsWith('/admin'),
    },
  ],
};

/**
 * "Team tools" section for EntreDash team accounts: the advisor timesheet
 * and/or the business development board, by role. Null for everyone else.
 */
export function teamSection(roles: string[]): NavSectionDef | null {
  const items: NavItem[] = [];
  if (roles.includes('consultant')) {
    items.push({
      key: 'timesheet',
      label: 'Timesheet',
      href: '/timesheet',
      glyph: g.clock,
      title: 'Log your advisor hours',
      isActive: ({ location }) => location.startsWith('/timesheet'),
    });
  }
  if (roles.includes('bd')) {
    items.push({
      key: 'bd',
      label: 'Business development',
      href: '/bd',
      glyph: g.kanban,
      title: 'Your business development opportunities and follow-ups',
      isActive: ({ location }) => location.startsWith('/bd'),
    });
  }
  if (!items.length) return null;
  return {
    id: 'team',
    label: 'Team tools',
    hint: 'Your EntreDash team tools: log advisor hours and manage business development opportunities.',
    items,
  };
}

/** Live-item count for a section header ("3"); falls back to the total when nothing is live yet. */
export function sectionCount(s: NavSectionDef): number {
  const counted = s.items.filter((i) => !i.uncounted);
  const live = counted.filter((i) => !i.soon).length;
  return live || counted.length;
}

/** "3 · 2 soon" meta line for mobile tiles. */
export function sectionMeta(s: NavSectionDef): string {
  const counted = s.items.filter((i) => !i.uncounted);
  const live = counted.filter((i) => !i.soon).length;
  const soon = counted.length - live;
  if (!live) return 'Soon';
  return soon ? `${live} · ${soon} soon` : String(live);
}

export const sectionContainsActive = (s: NavSectionDef, c: NavCtx) => s.items.some((i) => i.isActive(c));

/** Mobile bottom-tab matchers. */
export const isAdvisorsActive = (c: NavCtx) =>
  c.location === '/strategy' || NAV_SECTIONS.find((s) => s.id === 'advisors')!.items.filter((i) => !i.soon).some((i) => i.isActive(c));

/** My Notes with its views: a fixed row in the desktop sidebar's start group, a tile in the phone Menu. */
export const NOTES_SECTION: NavSectionDef = {
    id: 'notes',
    label: 'My Notes',
    hint: 'Quick notes and memo highlights in one place. Press N and switch to Note to jot one down.',
    items: [
      {
        key: 'notes-inbox',
        label: 'Notes inbox',
        href: '/notes?view=inbox',
        glyph: g.notes,
        title: 'Notes that are not on an idea yet.',
        isActive: ({ location }) => location === '/notes' && notesView() === 'inbox',
      },
      {
        key: 'notes-all',
        label: 'All notes',
        href: '/notes',
        glyph: g.notes,
        title: 'Every quick note and memo highlight, newest first, by idea.',
        isActive: ({ location }) => (location === '/notes' && !notesView()) || /^\/ideas\/[^/]+\/notes/.test(location),
      },
      {
        key: 'notes-manage',
        label: 'Manage notes',
        href: '/notes?view=manage',
        glyph: g.gear,
        title: 'Select several notes to archive, move or delete, and see archived ones.',
        isActive: ({ location }) => location === '/notes' && notesView() === 'manage',
      },
    ],
  };
